import React, { useState, useEffect } from 'react';
import Modal from "../clientes/ClienteModal";
import Swal from 'sweetalert';

const EnlazarMovientosAlmacen = ({ idAlmacen, idRegistroDeFlete, onGuardado }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [movimientos, setMovimientos] = useState([]);
    const [movimientosSeleccionados, setMovimientosSeleccionados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchMovimientos();
            if (idRegistroDeFlete) {
            fetchMovimientosAsociados();
            }
        }
    }, [isOpen]);

    const fetchMovimientos = async () => {
        setLoading(true);
        try {
            const hoy = new Date();
            const haceSieteDias = new Date(hoy);
            haceSieteDias.setDate(hoy.getDate() - 7);

            const fechaInicio = haceSieteDias.toISOString().split('T')[0];
            const fechaFin = hoy.toISOString().split('T')[0];

            const response = await fetch(`/api/movimientosalmacen/list/${idAlmacen}/${fechaInicio}/${fechaFin}`);
            const data = await response.json();

            const movimientosFiltrados = data[1].movs.filter(mov => mov.Estado !== 'Anulado');

            const movimientosYaEnlazados = await fetchMovimientosEnlazados(
                movimientosFiltrados.map(m => m.IdMovimientoAlmacen)
            );

            // Marcar los movimientos que están enlazados
            const movimientosConEstado = movimientosFiltrados.map(mov => ({
                ...mov,
                yaEnlazado: movimientosYaEnlazados.some(e => e.IdMovimientoAlmacen === mov.IdMovimientoAlmacen),
                registroFleteEnlazado: movimientosYaEnlazados.find(e => e.IdMovimientoAlmacen === mov.IdMovimientoAlmacen)?.NumeroRegistroFlete || null
            }));

            setMovimientos(movimientosConEstado);
        } catch (error) {
            console.error('Error al cargar movimientos:', error);
            Swal('Error', 'No se pudieron cargar los movimientos de almacén.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchMovimientosEnlazados = async (idsMovimientos) => {
        if (!idsMovimientos || idsMovimientos.length === 0) return [];
        
        try {
            const response = await fetch(`/api/registrosflete/movimientos/check-enlazados`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    idsMovimientosAlmacen: idsMovimientos,
                    idRegistroFleteExcluir: idRegistroDeFlete
                })
            });
            
            if (!response.ok) return [];
            
            const data = await response.json();
            return data.movimientosEnlazados || [];
        } catch (error) {
            console.error('Error al verificar movimientos enlazados:', error);
            return [];
        }
    };

    const fetchMovimientosAsociados = async () => {
        try {
            const response = await fetch(`/api/registrosflete/items/${idRegistroDeFlete}/movimientos`);
            const data = await response.json();

            if (data.ok && data.movimientos) {
                const idsAsociados = data.movimientos.map(mov => mov.IdMovimientoAlmacen);
                setMovimientosSeleccionados(idsAsociados);
            }
        } catch (error) {
            console.error('Error al cargar movimientos asociados:', error);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const disponibles = movimientos
                .filter(mov => !mov.yaEnlazado)
                .map(mov => mov.IdMovimientoAlmacen);
            setMovimientosSeleccionados(disponibles);
        } else {
            setMovimientosSeleccionados([]);
        }
    };

    const handleSelectMovimiento = (id, yaEnlazado) => {
        if (yaEnlazado) return;
        
        if (movimientosSeleccionados.includes(id)) {
            setMovimientosSeleccionados(movimientosSeleccionados.filter(movId => movId !== id));
        } else {
            setMovimientosSeleccionados([...movimientosSeleccionados, id]);
        }
    };

    const handleGuardar = async () => {
        if (!idRegistroDeFlete) {
            Swal('Advertencia', 'Debe seleccionar un registro de flete primero.', 'warning');
            return;
        }
        
        if (movimientosSeleccionados.length === 0) {
            const confirmar = await new Promise((resolve) => {
                Swal({
                    title: '¿Desvincular todos los movimientos?',
                    text: 'Esto eliminará todas las vinculaciones con movimientos de almacén.',
                    icon: 'warning',
                    buttons: {
                        cancel: {
                            text: 'Cancelar',
                            value: false,
                            visible: true,
                        },
                        confirm: {
                            text: 'Sí, desvincular',
                            value: true,
                        }
                    },
                    dangerMode: true,
                }).then(resolve);
            });

            if (!confirmar) {
                return;
            }
        }

        setGuardando(true);
        try {
            const response = await fetch(
                `/api/registrosflete/items/${idRegistroDeFlete}/movimientos`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        idsMovimientosAlmacen: movimientosSeleccionados
                    })
                }
            );

            const data = await response.json();

            if (data.ok) {
                const mensaje = movimientosSeleccionados.length === 0 
                    ? 'Se han desvinculado todos los movimientos correctamente'
                    : data.message || 'Movimientos enlazados correctamente';
                
                Swal('Éxito', mensaje, 'success');
                
                setIsOpen(false);
                
                if (onGuardado) {
                    onGuardado();
                }
            } else {
                Swal('Error', data.message || 'No se pudieron enlazar los movimientos.', 'error');
            }
        } catch (error) {
            console.error('Error al guardar movimientos:', error);
            Swal('Error', 'Ocurrió un error al guardar. Intente nuevamente.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    const handleCancelar = () => {
        setMovimientosSeleccionados([]);
        setIsOpen(false);
    };

    const handleAbrir = () => {
        setIsOpen(true);
    };

    const movimientosDisponibles = movimientos.filter(m => !m.yaEnlazado);
    const allSelected = movimientosDisponibles.length > 0 && 
                        movimientosSeleccionados.length === movimientosDisponibles.length;

    return (
        <>
            <button 
                className="mr-2 btn-verde-mfp-letra-normal" 
                onClick={handleAbrir}
                disabled={!idRegistroDeFlete}
                title={!idRegistroDeFlete ? 'Debe guardar el registro de flete primero' : 'Enlazar movimientos de almacén'}
            >
                Enlazar
            </button>
            <Modal isOpen={isOpen} title="Enlazar Movimientos Almacén" onClose={handleCancelar}>
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <label className="font-weight-bold">
                                Movimientos Almacén
                                {movimientosSeleccionados.length > 0 && (
                                    <span className="ml-2 badge badge-primary">
                                        {movimientosSeleccionados.length} seleccionado(s)
                                    </span>
                                )}
                            </label>
                            
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Cargando...</span>
                                    </div>
                                    <p className="mt-2">Cargando movimientos...</p>
                                </div>
                            ) : (
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-bordered table-striped bg-white table-sm mt-3">
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                            <tr>
                                                <th style={{ width: '50px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={allSelected}
                                                        onChange={handleSelectAll}
                                                        disabled={movimientos.length === 0}
                                                    />
                                                </th>
                                                <th>Cód. Movimiento</th>
                                                <th>Fecha</th>
                                                <th>Nro. Doc.</th>
                                                <th>Total</th>
                                                <th>Razón Social</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {movimientos.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center">
                                                        No hay movimientos disponibles
                                                    </td>
                                                </tr>
                                            ) : (
                                                movimientos.map(mov => (
                                                    <tr 
                                                        key={mov.IdMovimientoAlmacen}
                                                        className={mov.yaEnlazado ? 'table-secondary' : ''}
                                                        style={mov.yaEnlazado ? { opacity: 0.6 } : {}}
                                                    >
                                                        <td className="text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={movimientosSeleccionados.includes(mov.IdMovimientoAlmacen)}
                                                                onChange={() => handleSelectMovimiento(mov.IdMovimientoAlmacen, mov.yaEnlazado)}
                                                                disabled={mov.yaEnlazado}
                                                                title={mov.yaEnlazado ? `Ya enlazado con ${mov.registroFleteEnlazado}` : ''}
                                                            />
                                                        </td>
                                                        <td>
                                                            {mov.Numero}
                                                            {mov.yaEnlazado && (
                                                                <span className="badge badge-warning ml-2" title={`Enlazado con ${mov.registroFleteEnlazado}`}>
                                                                    Enlazado
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>{mov.FechaMovimiento}</td>
                                                        <td>{mov.NroDocumentoSunat}</td>
                                                        <td>S/ {mov.Total}</td>
                                                        <td>{mov.RazonSocial}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="mt-4 d-flex justify-content-between align-items-center">
                                <div>
                                    {movimientosSeleccionados.length === 0 && movimientos.length > 0 && (
                                        <small className="text-muted">
                                            <i className="fas fa-info-circle mr-1"></i>
                                            Guardar sin selecciones desvinculará todos los movimientos
                                        </small>
                                    )}
                                </div>
                                <div>
                                    <button 
                                        className="btn btn-secondary mr-2"
                                        onClick={handleCancelar}
                                        disabled={guardando}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        className="btn btn-primary"
                                        onClick={handleGuardar}
                                        disabled={guardando}
                                    >
                                        {guardando ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                Guardando...
                                            </>
                                        ) : 'Guardar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EnlazarMovientosAlmacen;
